/**
 * Hook React Query para comentarios por artefacto del Action Center (0675 F3 piloto multi-modelo #2).
 *
 * Endpoints backend (ADR-029 implementacion · BACKLOG [0666b][DETAIL-COMMENTS]):
 *   GET    /api/v1/artefacto-comentarios?artefactoTipo=...&artefactoId=...
 *   POST   /api/v1/artefacto-comentarios                                     (JSON body · NO multipart)
 *   DELETE /api/v1/artefacto-comentarios/:id                                  (soft-delete)
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { request } from '../../../shared/services/api';
import type { ArtefactoWorkflowTipo } from './useActionCenterDetail';

export interface ArtefactoComentario {
  id: string;
  tenantId: string;
  artefactoTipo: ArtefactoWorkflowTipo;
  artefactoId: string;
  contenido: string;
  subidoPor: string;
  creadoEn: string;
  eliminadoEn: string | null;
}

const QUERY_KEY = (tipo: ArtefactoWorkflowTipo, id: string) =>
  ['artefactoComentarios', tipo, id] as const;

export function useArtefactoComentarios(
  artefactoTipo: ArtefactoWorkflowTipo | null,
  artefactoId: string | null,
  enabled = true,
) {
  return useQuery({
    queryKey: QUERY_KEY(artefactoTipo!, artefactoId!),
    queryFn: async (): Promise<ArtefactoComentario[]> => {
      const params = new URLSearchParams({
        artefactoTipo: artefactoTipo!,
        artefactoId: artefactoId!,
      });
      const res = await request<{ data: ArtefactoComentario[] }>(
        `/artefacto-comentarios?${params.toString()}`,
      );
      return res.data;
    },
    enabled: enabled && !!artefactoTipo && !!artefactoId,
    staleTime: 30_000,
  });
}

export function useCrearComentario(artefactoTipo: ArtefactoWorkflowTipo, artefactoId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (contenido: string): Promise<ArtefactoComentario> => {
      // request() helper canonico (POST JSON · helper adjunta credencial sesion automatico).
      // No usamos fetch manual porque NO hay multipart aqui (leccion 0674 HIGH-1 aplicada al reves:
      // si fuera multipart, fetch manual; aqui es JSON puro, helper canonico).
      const res = await request<{ data: ArtefactoComentario }>('/artefacto-comentarios', {
        method: 'POST',
        body: JSON.stringify({ artefactoTipo, artefactoId, contenido }),
      });
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY(artefactoTipo, artefactoId) });
    },
  });
}

export function useEliminarComentario(artefactoTipo: ArtefactoWorkflowTipo, artefactoId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (comentarioId: string): Promise<void> => {
      await request(`/artefacto-comentarios/${comentarioId}`, { method: 'DELETE' });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY(artefactoTipo, artefactoId) });
    },
  });
}

export interface TiempoRelativoLabels {
  ahora: string;
  haceMinutos: string; // "hace {n}m"
  haceHoras: string; // "hace {n}h"
  haceDias: string; // "hace {n}d"
}

// Fix MED-2 Codex 0675: i18n del tiempo relativo (antes hardcoded en es).
// Recibe los labels desde el componente (que los obtiene de useI18n) +
// locale BCP-47 para toLocaleDateString fallback (mas de 7 dias).
export function formatTiempoRelativo(
  iso: string,
  labels: TiempoRelativoLabels,
  locale: string,
): string {
  const fecha = new Date(iso);
  const ahora = Date.now();
  const diffMs = ahora - fecha.getTime();
  const diffSeg = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSeg / 60);
  const diffHor = Math.floor(diffMin / 60);
  const diffDia = Math.floor(diffHor / 24);

  if (diffSeg < 60) return labels.ahora;
  if (diffMin < 60) return labels.haceMinutos.replace('{n}', String(diffMin));
  if (diffHor < 24) return labels.haceHoras.replace('{n}', String(diffHor));
  if (diffDia < 7) return labels.haceDias.replace('{n}', String(diffDia));
  return fecha.toLocaleDateString(locale);
}
